import { Button } from 'aimo-ui';
import { MenuItemWrapped } from 'components/account/menuItems/MenuComponents';
import WalletAddedBadge from 'components/payment/WalletAddedBadge';
import useAddPaymentMethod from 'hooks/useAddPaymentMethod';
import useCanAddMobilePay from 'hooks/useCanAddMobilePay';
import useWalletAddRows from 'hooks/useWalletAddRows';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { StyledWrapper } from 'styles/ContainerStyles';
import {
  getPaymentMethodLabel,
  getPaymentMethodLogo,
  resolvePaymentMethodsAddedLabel,
} from 'utils/paymentUtils';
import { useUiStore } from 'stores/uiStore';

/**
 * Displays a modal that allows users to select and add an additional payment
 * method: MobilePay, a stored wallet (Apple Pay / Google Pay), or a credit
 * card. Rows self-gate — MobilePay per useCanAddMobilePay, wallets per
 * useWalletAddRows — so the modal renders correctly for any combination
 * the entry point routed here for. Tapping a wallet row runs the Adyen init
 * directly: the platform pay sheet is the approval surface, there is no
 * separate consent step. An Active/Verifying/Blocked/fresh-Adding Adyen
 * wallet of a given type shows a non-selectable "Added" state instead of
 * starting another add.
 */
export const AddOtherPaymentMethod: React.FC = () => {
  const { t } = useTranslation();
  const addedLabel = resolvePaymentMethodsAddedLabel(t);
  const {
    addCard,
    addWallet,
    loadingAddCard,
    loadingAddMobilePay,
    pendingWalletType,
  } = useAddPaymentMethod();
  const canAddMobilePay = useCanAddMobilePay();
  const walletRows = useWalletAddRows();

  return (
    <StyledWrapper mb={0}>
      {canAddMobilePay && (
        <MenuItemWrapped
          key={`mobile-pay-button`}
          menuLabel={getPaymentMethodLabel('MOBILEPAY', t)}
          iconName={getPaymentMethodLogo('MOBILEPAY')?.logoName}
          large={true}
          testID={`mobile-pay-button`}
          onPress={() =>
            useUiStore.getState().setSheetModal('mobilePayConfirmation')
          }
          withArrow={true}
          loading={loadingAddMobilePay}
        />
      )}
      {walletRows.map(
        ({ walletType, alreadyAdded, labelKey, icon, testID }) => {
          const walletMenuLabel = t(labelKey);
          return (
            <MenuItemWrapped
              key={testID}
              menuLabel={walletMenuLabel}
              iconName={icon}
              large={true}
              testID={testID}
              onPress={alreadyAdded ? undefined : () => addWallet(walletType)}
              withArrow={!alreadyAdded}
              accessibilityLabel={
                alreadyAdded ? `${walletMenuLabel}. ${addedLabel}` : undefined
              }
              rightComponent={
                alreadyAdded ? (
                  <WalletAddedBadge
                    label={addedLabel}
                    testID={`${testID}-added`}
                  />
                ) : (
                  false
                )
              }
              loading={!alreadyAdded && pendingWalletType === walletType}
            />
          );
        }
      )}
      <MenuItemWrapped
        menuLabel={t('payment.creditCardOnly')}
        iconName="Credit_Card-Outlined"
        large={true}
        testID="credit-card-button"
        onPress={() => addCard()}
        withArrow={true}
        loading={loadingAddCard}
        noBorder
      />
      <StyledWrapper mb={25}>
        <Button
          testID="cancel-button"
          accessibilityLabel={t('button.cancel')}
          size="l"
          text={t('button.cancel')}
          onPress={() => {
            useUiStore.getState().setSheetModal('');
          }}
          disabled={false}
          buttonType="tertiary"
        />
      </StyledWrapper>
    </StyledWrapper>
  );
};
export default AddOtherPaymentMethod;
